import { createSlice } from "@reduxjs/toolkit";
import { closeDeleteModal, closeDocumentInfoModal, closeUnlockModal, delPack, fetchInprogressAudios, fetchPackDetail, fetchPaginateInprogressAudios, fetchRefreshInProgress, openDeleteModal, openDocumentInfoModal, openUnlockModal, resetInProgressTable, resetPackRequestor, setCurrentRecord, setInProgressAutoRefresh, setIsEditedForDocumentInfoModal, stopInProgressAutoRefresh, switchInProgressAutoFresh, unlockPack, updateInProgressRequestBody, updatePack } from "./InProgressAction";
import { defaultInProgressRequestBody, DefaultInProgressRequestBodyType } from "../../../constant/document";
import { PackType } from "../../../utils/type";

export interface PaginationType {
  page: number;
  pageSize: number;
  hasMore: boolean;
  loading: boolean;
}

export interface Pack {
  data: any;
  loading: boolean;
  isEdited: boolean;

  idPack: number;
  uuid: string;
  tenantId: number;
  tenantName: string;
  statusId: number;
  status: string;
  priorityId: number;
  priority: string;
  transcriptionTypeId: number;
  transcriptionType: string;
  createdDate: string;
  createdBy: string;
  fileName: string;
  errorReason: string;
  assignedUserId: number;
  assignedUser: string;
  assignedUserEmail: string;
  lockedUserId: number;
  lockedUser: string;
  lockedUserEmail: string;
  clientName: string;
  jobNumber: string;
  lockedTo: string;
}

interface AutoRefreshStateType {
  autoRefresh: boolean;
  currentIntervalId: NodeJS.Timer | undefined;
  error: string | undefined;
  loading: boolean;
}

interface InProgressAudiosStateType {
  data: any;
  loading: boolean;
  error: string | undefined;
  currentRequestBody: DefaultInProgressRequestBodyType;
  unlockLoading: boolean;
  delJobLoading: boolean;
  currentRecord: any;
  pagination: PaginationType;
  autoRefreshInProgressState: AutoRefreshStateType;
  success: boolean;
  message: string;
  currentTenantId: number;
  packState: Pack;
  //Modal
  documentInfoModalState: boolean;
  deleteModalState: boolean;
  unlockModalState: boolean;
}

const InProgressAudiosInitialState: InProgressAudiosStateType = {
  data: [],
  loading: false,
  error: undefined,
  unlockLoading: false,
  delJobLoading: false,
  currentRecord: {},
  currentRequestBody: defaultInProgressRequestBody,
  pagination: {
    page: 2,
    pageSize: 30,
    hasMore: true,
    loading: false,
  },
  autoRefreshInProgressState: {
    autoRefresh: false,
    currentIntervalId: undefined,
    error: "",
    loading: false,
  },
  success: false,
  message: "",
  currentTenantId: 0,

  packState: {
    data: [],
    loading: false,
    isEdited: false,
    idPack: 0,
    uuid: "",
    tenantId: 0,
    tenantName: "",
    statusId: 0,
    status: "",
    priorityId: 0,
    priority: "",
    transcriptionTypeId: 1,
    transcriptionType: "",
    createdDate: "",
    createdBy: "",
    fileName: "",
    errorReason: "",
    assignedUserId: 0,
    assignedUser: "",
    assignedUserEmail: "",
    lockedUserId: 0,
    lockedUser: "",
    lockedUserEmail: "",
    clientName: "",
    jobNumber: "",
    lockedTo: "",
  },

  //Modal
  documentInfoModalState: false,
  deleteModalState: false,
  unlockModalState: false,
};

const InprogressAudiosSlice = createSlice({
  initialState: InProgressAudiosInitialState,
  name: "transcribe",
  reducers: {},
  extraReducers(builder) {
    builder
      //Fetch Inprogress data from API
      .addCase(fetchInprogressAudios.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchInprogressAudios.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchInprogressAudios.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })

      //Infinite Scrolling
      .addCase(fetchPaginateInprogressAudios.pending, (state, action) => {
        state.pagination.loading = true;
      })
      .addCase(fetchPaginateInprogressAudios.fulfilled, (state, action) => {
        state.pagination.page = state.pagination.page + 1;
        state.data = state.data.concat(action.payload.data);
        state.pagination.hasMore = action.payload.data.length === state.pagination.pageSize;
        state.pagination.loading = false;
      })
      .addCase(fetchPaginateInprogressAudios.rejected, (state, action) => {
        state.error = action.error.message;
        state.pagination.loading = false;
      })

      //Search InInProgress
      .addCase(updateInProgressRequestBody, (state, action) => {
        state.currentRequestBody = action.payload;
      })
      .addCase(resetInProgressTable, (state, action) => {
        return {
          ...InProgressAudiosInitialState,
          data: state.data,
          autoRefreshInProgressState: state.autoRefreshInProgressState,
        };
      })

      //  Refresh
      .addCase(fetchRefreshInProgress.pending, (state, action) => {
        state.autoRefreshInProgressState.loading = true;
      })
      .addCase(fetchRefreshInProgress.fulfilled, (state, action) => {
        state.data = action.payload;
        state.autoRefreshInProgressState.loading = false;
      })
      .addCase(fetchRefreshInProgress.rejected, (state, action) => {
        state.autoRefreshInProgressState.error = action.error.message;
        state.autoRefreshInProgressState.loading = false;
      })

      //Auto Refresh
      .addCase(setInProgressAutoRefresh, (state, action) => {
        state.autoRefreshInProgressState.currentIntervalId = action.payload.intervalId;
      })
      .addCase(switchInProgressAutoFresh, (state, action) => {
        state.autoRefreshInProgressState.autoRefresh = action.payload.autoRefresh;
      })
      .addCase(stopInProgressAutoRefresh, (state, action) => {
        clearInterval(state.autoRefreshInProgressState.currentIntervalId);
      })

      //Fetching each packDeatils
      .addCase(fetchPackDetail.pending, (state, action) => {
        state.packState.loading = true;
      })
      .addCase(fetchPackDetail.fulfilled, (state, action) => {
        state.packState.data = action.payload;
        state.packState.loading = false;
      })
      .addCase(fetchPackDetail.rejected, (state, action) => {
        state.error = action.error.message;
        state.packState.loading = false;
      })

      //Save Enable and Disable
      .addCase(setIsEditedForDocumentInfoModal, (state, action) => {
        state.packState.isEdited = action.payload;
      })

      //Update Pack and Edit
      .addCase(updatePack.pending, (state, action) => {
        state.packState.loading = true;
      })
      .addCase(updatePack.fulfilled, (state, action) => {
        state.packState.data = {
          ...state.packState.data,
          ...action.payload,
        };
        state.packState.loading = false;
      })
      .addCase(updatePack.rejected, (state, action) => {
        state.error = action.error.message;
        state.packState.loading = false;
      })

      //Del Pack
      .addCase(delPack.pending, (state, action) => {
        state.delJobLoading = true;
      })
      .addCase(delPack.fulfilled, (state, action) => {
        state.delJobLoading = false;
      })
      .addCase(delPack.rejected, (state, action) => {
        state.error = action.error.message;
        state.delJobLoading = false;
      })

      //Unlock Pack
      .addCase(unlockPack.pending, (state, action) => {
        state.unlockLoading = true;
      })
      .addCase(unlockPack.fulfilled, (state, action) => {
        state.unlockLoading = false;
        state.packState.data = action.payload;
      })
      .addCase(unlockPack.rejected, (state, action) => {
        state.error = action.error.message;
        state.unlockLoading = false;
      })

      //Modal
      .addCase(openDocumentInfoModal, (state, action) => {
        state.documentInfoModalState = true;
      })
      .addCase(closeDocumentInfoModal, (state, action) => {
        state.documentInfoModalState = false;
      })

      .addCase(openDeleteModal, (state, action) => {
        state.deleteModalState = true;
      })
      .addCase(closeDeleteModal, (state, action) => {
        state.deleteModalState = false;
      })

      .addCase(openUnlockModal, (state, action) => {
        state.unlockModalState = true;
      })
      .addCase(closeUnlockModal, (state, action) => {
        state.unlockModalState = false;
      })

      .addCase(setCurrentRecord, (state, action) => {
        state.currentRecord = action.payload;
      });
  },
});

export default InprogressAudiosSlice.reducer;
